import { useMemo } from "react";
import { addDays } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { useUnifiedAuth } from "@/hooks/useUnifiedAuth";
import { useAppSelector } from "@/store/hooks";
import {
  buildCollectsFilter,
  mapSummaryItemToTable,
  useCollectsApiData,
  useCollectsScope,
  type CollectForTable,
} from "@/hooks/useCollectsApiData";
import type { FilterState } from "@/hooks/useFilters";
import {
  fetchCollectsCalendar,
  fetchNextScheduledCollect,
} from "@/integrations/agreements/client";
import {
  type DateWindow,
  dayKey,
  groupCollectsByDay,
  parseCollectDate,
  startOfToday,
} from "@/utils/coletasCalendar";

const RESUMO_STALE = 2 * 60 * 1000;
const RESUMO_GC = 10 * 60 * 1000;

/** Só coletas agendadas/futuras alimentam a aba Próximas Coletas. */
export const PROXIMAS_STATUS = ["planned"];

/** Referência estável para o caso vazio (evita refazer o agrupamento a cada render). */
const EMPTY_COLLECTS: CollectForTable[] = [];

/** Preview por dia no calendário: até 5 coletas + total do dia (para o "Ver mais"). */
export interface DiaCalendario {
  collects: CollectForTable[];
  total: number;
}

const EMPTY_BY_DAY = new Map<string, DiaCalendario>();

export interface UseColetasCalendarParams {
  window: DateWindow;
  filters?: FilterState;
  enabled?: boolean;
}

/**
 * Calendário das visões Semana/Mês: endpoint dedicado que retorna, por dia,
 * até 5 coletas (preview) e o total de coletas do dia. Substitui o
 * carregamento client-side de todas as coletas da janela.
 */
export function useColetasCalendar({
  window,
  filters,
  enabled = true,
}: UseColetasCalendarParams) {
  const { profile } = useUnifiedAuth();
  const generatorsFromRedux = useAppSelector((s) => s.establishmentAccess.generators);
  const establishmentsFromRedux = useAppSelector((s) => s.establishmentAccess.establishments);

  const apiFilter = buildCollectsFilter(
    filters,
    profile,
    generatorsFromRedux,
    establishmentsFromRedux,
    1,
    1,
    { status: PROXIMAS_STATUS, orderDirection: "asc", window, excludePlannedMaterialIds: [4, 5, 26] },
  );

  const query = useQuery({
    queryKey: ["collects-calendar", apiFilter],
    queryFn: () => fetchCollectsCalendar(apiFilter),
    enabled: !!profile && enabled,
    staleTime: RESUMO_STALE,
    gcTime: RESUMO_GC,
  });

  const byDay = useMemo(() => {
    const days = query.data;
    if (!days?.length) return EMPTY_BY_DAY;
    const map = new Map<string, DiaCalendario>();
    for (const day of days) {
      const parsed = parseCollectDate(day.date);
      if (!parsed) continue;
      map.set(dayKey(parsed), {
        total: day.total,
        collects: day.collects.map(mapSummaryItemToTable),
      });
    }
    return map;
  }, [query.data]);

  return {
    byDay,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

/** Tamanho de página do drawer "coletas do dia". */
export const DIA_PAGE_SIZE = 20;

export interface UseDiaColetasParams {
  /** Dia (YYYY-MM-DD) cujas coletas serão listadas. null = drawer fechado. */
  day: string | null;
  filters?: FilterState;
  page: number;
  enabled?: boolean;
}

/**
 * Lista paginada de TODAS as coletas de um dia, com os mesmos filtros da
 * listagem. Reusa a query Collects com janela de 1 dia (date_from = date_to).
 */
export function useDiaColetas({
  day,
  filters,
  page,
  enabled = true,
}: UseDiaColetasParams) {
  const parsed = day ? parseCollectDate(day) : null;
  const query = useCollectsApiData({
    filters,
    window: parsed ? { start: parsed, end: parsed } : null,
    status: PROXIMAS_STATUS,
    orderDirection: "asc",
    page,
    pageSize: DIA_PAGE_SIZE,
    excludePlannedMaterialIds: [4, 5, 26],
    enabled: enabled && !!parsed,
  });

  const collects: CollectForTable[] = query.data?.data ?? EMPTY_COLLECTS;

  return {
    collects,
    pagination: query.data?.pagination,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

/** Tamanho de página da visão Agenda (lista paginada). */
export const AGENDA_PAGE_SIZE = 20;

export interface UseAgendaColetasParams {
  filters?: FilterState;
  page: number;
  enabled?: boolean;
}

/** Agenda: lista paginada de coletas planejadas (de hoje em diante), agrupada por dia. */
export function useAgendaColetas({
  filters,
  page,
  enabled = true,
}: UseAgendaColetasParams) {
  const today = startOfToday();

  const query = useCollectsApiData({
    filters,
    window: { start: today, end: addDays(today, 365) },
    status: PROXIMAS_STATUS,
    orderDirection: "asc",
    page,
    pageSize: AGENDA_PAGE_SIZE,
    excludePlannedMaterialIds: [4, 5, 26],
    enabled,
  });

  const collects: CollectForTable[] = query.data?.data ?? EMPTY_COLLECTS;
  const byDay = useMemo(() => groupCollectsByDay(collects), [collects]);

  return {
    byDay,
    pagination: query.data?.pagination,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

export interface UseProximasResumoParams {
  filters?: FilterState;
  enabled?: boolean;
}

/** Cards de resumo: Próxima Coleta (endpoint dedicado) e Total na Semana (endpoint dedicado). */
export function useProximasResumo({
  filters,
  enabled = true,
}: UseProximasResumoParams) {
  const { profile, scope } = useCollectsScope(filters);

  const nextFilter = { ...scope, date_from: dayKey(startOfToday()), exclude_planned_material_ids: [4, 5, 26] };

  const proximaQuery = useQuery({
    queryKey: ["next-scheduled-collect", nextFilter],
    queryFn: () => fetchNextScheduledCollect(nextFilter),
    enabled: !!profile && enabled,
    staleTime: RESUMO_STALE,
    gcTime: RESUMO_GC,
  });

  return {
    proximaColeta: proximaQuery.data ?? null,
    isLoading: proximaQuery.isLoading,
  };
}
